var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { observable } from "mobx";
import { delayAndWait } from "healthex-common/utils";
export class AIGeneratedFieldsModel {
    constructor(studyManager, updateInput) {
        this.studyManager = studyManager;
        this.patientFacingDetailsMetadata = {};
        this.isAIGenerationRequired = async (studyId, input) => {
            const createPatientFacingDetailsStudyInput = {
                title: input.title,
                briefSummary: input.briefSummary,
                description: input.description,
                primaryDiseaseOrConditions: input.primaryDiseaseOrConditions,
                inclusionExclusionCriteria: input.inclusionExclusionCriteria,
            };
            return await this.studyManager.isAIGenerationRequired(studyId, createPatientFacingDetailsStudyInput);
        };
        this.isRegenerating = false;
        this.updateInput = updateInput;
    }
    reset() {
        this.patientFacingDetailsMetadata = {};
    }
    canGeneratePatientFacingDetails(input) {
        var _a, _b;
        return (!!input.title &&
            !!input.briefSummary &&
            ((_b = (_a = input.primaryDiseaseOrConditions) === null || _a === void 0 ? void 0 : _a.some((condition) => condition.length > 0)) !== null && _b !== void 0 ? _b : false) &&
            !!input.inclusionExclusionCriteria);
    }
    getPatientFacingDetailCounter(field) {
        const detail = this.patientFacingDetailsMetadata[field];
        if (!detail)
            return null;
        return `${detail.startIndex} / ${detail.endIndex}`;
    }
    isPatientFacingDetailRestoreAvailable(field) {
        const detail = this.patientFacingDetailsMetadata[field];
        if (!detail)
            return false;
        return detail.backup[detail.currentIndex] !== undefined;
    }
    isPatientFacingDetailAvailable(field) {
        var _a, _b, _c;
        return !((_a = this.patientFacingDetailsMetadata[field]) === null || _a === void 0 ? void 0 : _a.failed) && ((_c = (_b = this.patientFacingDetailsMetadata[field]) === null || _b === void 0 ? void 0 : _b.choices.length) !== null && _c !== void 0 ? _c : 0) > 0;
    }
    patientFacingDetailRegenerateDisabledReason(field) {
        var _a, _b;
        if (((_a = this.patientFacingDetailsMetadata[field]) === null || _a === void 0 ? void 0 : _a.endIndex) === ((_b = this.patientFacingDetailsMetadata[field]) === null || _b === void 0 ? void 0 : _b.total)) {
            return "NO_MORE";
        }
        else if (this.isRegenerating) {
            return "REGENERATING";
        }
        return null;
    }
    isPreviousButtonDisabled(field) {
        var _a;
        return ((_a = this.patientFacingDetailsMetadata[field]) === null || _a === void 0 ? void 0 : _a.startIndex) === 1;
    }
    isNextButtonDisabled(field) {
        var _a, _b;
        return ((_a = this.patientFacingDetailsMetadata[field]) === null || _a === void 0 ? void 0 : _a.startIndex) === ((_b = this.patientFacingDetailsMetadata[field]) === null || _b === void 0 ? void 0 : _b.endIndex);
    }
    patientFacingDetailTotalChoices(field) {
        var _a, _b;
        return (_b = (_a = this.patientFacingDetailsMetadata[field]) === null || _a === void 0 ? void 0 : _a.total) !== null && _b !== void 0 ? _b : 0;
    }
    async regeneratePatientFacingDetail(field) {
        const detail = this.patientFacingDetailsMetadata[field];
        if (!detail)
            return;
        this.isRegenerating = true;
        await delayAndWait(1000);
        if (detail.endIndex < detail.total) {
            this.updateInput({
                [field]: detail.choices[detail.endIndex],
            });
            this.updateDetail(field, {
                currentIndex: detail.endIndex,
                startIndex: detail.endIndex + 1,
                endIndex: detail.endIndex + 1,
            });
        }
        this.isRegenerating = false;
    }
    previousPatientFacingDetailChoice(field) {
        const detail = this.patientFacingDetailsMetadata[field];
        if (!detail)
            return;
        if (detail.startIndex > 1) {
            detail.startIndex--;
            this.updateInput({
                [field]: detail.choices[detail.startIndex - 1],
            });
            this.updateDetail(field, {
                currentIndex: detail.startIndex - 1,
                startIndex: detail.startIndex,
            });
        }
    }
    nextPatientFacingDetailChoice(field) {
        const detail = this.patientFacingDetailsMetadata[field];
        if (!detail)
            return;
        if (detail.startIndex < detail.endIndex) {
            detail.startIndex++;
            this.updateInput({
                [field]: detail.choices[detail.startIndex - 1],
            });
            this.updateDetail(field, {
                currentIndex: detail.startIndex - 1,
                startIndex: detail.startIndex,
            });
        }
    }
    restoreToOriginalPatientFacingDetail(field) {
        const detail = this.patientFacingDetailsMetadata[field];
        if (!detail)
            return;
        if (detail.backup[detail.currentIndex]) {
            detail.choices[detail.currentIndex] = detail.backup[detail.currentIndex];
            this.updateInput({
                [field]: detail.choices[detail.currentIndex],
            });
            delete detail.backup[detail.currentIndex];
            this.updateDetail(field, {
                backup: detail.backup,
                choices: detail.choices,
            });
        }
    }
    removePatientFacingDetailChoice(field) {
        const detail = this.patientFacingDetailsMetadata[field];
        if (!detail)
            return;
        if (!detail.backup[detail.currentIndex]) {
            detail.backup[detail.currentIndex] = detail.choices[detail.currentIndex];
        }
        detail.choices[detail.currentIndex] = "";
        this.updateInput({
            [field]: "",
        });
        this.updateDetail(field, {
            backup: detail.backup,
            choices: detail.choices,
        });
    }
    editPatientFacingDetailChoice(field, value) {
        this.updateInput({
            [field]: value,
        });
        const detail = this.patientFacingDetailsMetadata[field];
        if (!detail)
            return;
        if (detail.choices.length === 0)
            return;
        if (!detail.backup[detail.currentIndex]) {
            detail.backup[detail.currentIndex] = detail.choices[detail.currentIndex];
            detail.choices[detail.currentIndex] = value;
        }
        else {
            detail.choices[detail.currentIndex] = value;
        }
        this.updateDetail(field, {
            backup: detail.backup,
            choices: detail.choices,
        });
    }
    addDetail(fieldName, data) {
        this.patientFacingDetailsMetadata[fieldName] = data;
    }
    updateDetail(fieldName, data) {
        const fieldValue = this.patientFacingDetailsMetadata[fieldName];
        if (!fieldValue)
            return;
        this.patientFacingDetailsMetadata[fieldName] = { ...fieldValue, ...data };
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], AIGeneratedFieldsModel.prototype, "patientFacingDetailsMetadata", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AIGeneratedFieldsModel.prototype, "isRegenerating", void 0);
